/**
 * MoveOrCopyDialog.jsx
 *
 * Reusable dialog for moving or copying a document to another directory.
 * - Navigates the full client tree (client + companies) to pick a destination
 * - Move is implemented as Copy + Delete
 * - Each picker entry carries `ownerId` = the client/company id that owns that branch,
 *   which becomes `targetAssignedTo` in the CopyDocument API call.
 */

import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Breadcrumbs,
  Link,
  IconButton,
  Button,
  Divider,
  CircularProgress,
  Alert
} from '@mui/material'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useTranslation } from 'react-i18next'
import { apiUrl } from '../../../config/api'

// ---------------------------------------------------------------------------
const brandColor = () => (window.$brand === 'PRB' ? '#17afb6' : '#102a46')
const brandRadius = () => (window.$brand === 'PRB' ? 0 : 2)
const lang = (en, es) => (window.$lang === 'en' ? en : es || en)

// ---------------------------------------------------------------------------
// DestinationRow — navigable folder (not yet selectable)
// ---------------------------------------------------------------------------
const DestinationRow = ({ label, meta, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      px: 2,
      py: 1.25,
      borderBottom: '1px solid',
      borderColor: 'divider',
      cursor: 'pointer',
      '&:hover': { bgcolor: 'action.hover' },
      transition: 'background 0.1s'
    }}
  >
    <FolderOutlinedIcon
      fontSize='small'
      sx={{ color: 'text.secondary', mr: 1.5, flexShrink: 0 }}
    />
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography variant='body2' noWrap>
        {label}
      </Typography>
      {meta && (
        <Typography variant='caption' color='text.secondary' noWrap>
          {meta}
        </Typography>
      )}
    </Box>
    <ChevronRightIcon
      sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }}
    />
  </Box>
)

// SelectableRow — at subcategory level, can be chosen as destination or drilled into
const SelectableRow = ({ label, meta, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      px: 2,
      py: 1.25,
      borderBottom: '1px solid',
      borderColor: 'divider',
      cursor: 'pointer',
      bgcolor: selected ? `${brandColor()}14` : 'transparent',
      '&:hover': { bgcolor: selected ? `${brandColor()}22` : 'action.hover' },
      transition: 'background 0.1s'
    }}
  >
    <FolderOutlinedIcon
      fontSize='small'
      sx={{
        color: selected ? brandColor() : 'text.secondary',
        mr: 1.5,
        flexShrink: 0
      }}
    />
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography variant='body2' noWrap fontWeight={selected ? 600 : 400}>
        {label}
      </Typography>
      {meta && (
        <Typography variant='caption' color='text.secondary' noWrap>
          {meta}
        </Typography>
      )}
    </Box>
    {selected && (
      <CheckCircleOutlineIcon
        sx={{ fontSize: 18, color: brandColor(), flexShrink: 0 }}
      />
    )}
  </Box>
)

// ---------------------------------------------------------------------------
// MoveOrCopyDialog
// ---------------------------------------------------------------------------
export const MoveOrCopyDialog = ({
  open,
  userId,
  mode = 'copy',
  document,
  client, // full client object from clientContext.data
  onClose,
  onSuccess, // (mode) => void
  onDeleteDoc // (docId) => void — called after move before close
}) => {
  const { t } = useTranslation()

  // Each stack entry: { type, label, ownerId, ...type-specific data }
  // ownerId = client.id or company.id — passed as targetAssignedTo to the API
  const [pickerStack, setPickerStack] = useState([])

  // Confirmed leaf: { subcategoryId, fileId?, ownerId, label }
  const [destination, setDestination] = useState(null)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (open) {
      setPickerStack([])
      setDestination(null)
      setError(null)
      setLoading(false)
      setDone(false)
    }
  }, [open])

  if (!document || !client) return null

  const pickerCurrent = pickerStack[pickerStack.length - 1] ?? null
  const push = (entry) => setPickerStack((p) => [...p, entry])
  const popTo = (index) => {
    setPickerStack((p) => p.slice(0, index + 1))
    setDestination(null)
  }
  const reset = () => {
    setPickerStack([])
    setDestination(null)
  }

  // ---------------------------------------------------------------------------
  // Render rows for the current picker level
  // ---------------------------------------------------------------------------
  const renderPickerLevel = () => {
    // ROOT — client row + company rows
    if (!pickerCurrent) {
      const companiesLabel =
        window.$brand === 'ZAP'
          ? t('Properties')
          : window.$brand === 'IDB'
            ? t('Financings')
            : t('Companies')

      return (
        <>
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Typography
              variant='caption'
              color='text.secondary'
              fontWeight={600}
            >
              {t('Client')}
            </Typography>
          </Box>
          <DestinationRow
            label={client.name}
            onClick={() =>
              push({ type: 'client', label: client.name, ownerId: client.id })
            }
          />
          {(client.companies || []).length > 0 && (
            <>
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  fontWeight={600}
                >
                  {companiesLabel}
                </Typography>
              </Box>
              {client.companies.map((company) => (
                <DestinationRow
                  key={company.id}
                  label={company.name}
                  onClick={() =>
                    push({
                      type: 'company',
                      label: company.name,
                      ownerId: company.id,
                      company
                    })
                  }
                />
              ))}
            </>
          )}
        </>
      )
    }

    // CLIENT → workspaces
    if (pickerCurrent.type === 'client') {
      return (client.workspaces || []).map((ws) => (
        <DestinationRow
          key={ws.name_en}
          label={lang(ws.name_en, ws.name_es)}
          onClick={() =>
            push({
              type: 'workspace',
              label: lang(ws.name_en, ws.name_es),
              ownerId: pickerCurrent.ownerId, // client.id
              workspace: ws
            })
          }
        />
      ))
    }

    // COMPANY → workspaces
    if (pickerCurrent.type === 'company') {
      return (pickerCurrent.company.workspaces || []).map((ws) => (
        <DestinationRow
          key={ws.name_en}
          label={lang(ws.name_en, ws.name_es)}
          onClick={() =>
            push({
              type: 'workspace',
              label: lang(ws.name_en, ws.name_es),
              ownerId: pickerCurrent.ownerId, // company.id carried forward
              workspace: ws
            })
          }
        />
      ))
    }

    // WORKSPACE → subcategories (navigable, not selectable yet)
    if (pickerCurrent.type === 'workspace') {
      return (pickerCurrent.workspace.subcategories || []).map((sub) => (
        <DestinationRow
          key={sub.subcategory_en}
          label={lang(sub.subcategory_en, sub.subcategory_es)}
          onClick={() => {
            const label = lang(sub.subcategory_en, sub.subcategory_es)
            const ownerId = pickerCurrent.ownerId
            // Pre-select the subcategory itself as soon as the user drills into it
            setDestination({
              subcategoryId: sub.id,
              fileId: null,
              ownerId,
              label
            })
            push({
              type: 'subcategory',
              label,
              ownerId,
              subcategory: sub,
              workspace: pickerCurrent.workspace
            })
          }}
        />
      ))
    }

    // SUBCATEGORY — selectable directly, or drill into a custom directory (file)
    if (pickerCurrent.type === 'subcategory') {
      const { subcategory, ownerId } = pickerCurrent
      const files = subcategory.files || []
      const subcatSelected =
        destination?.subcategoryId === subcategory.id && !destination?.fileId

      return (
        <>
          {/* Tapping the subcategory row itself selects it */}
          <Box
            sx={{
              px: 2,
              py: 1,
              bgcolor: subcatSelected ? `${brandColor()}14` : 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: `${brandColor()}0d` }
            }}
            onClick={() =>
              setDestination({
                subcategoryId: subcategory.id,
                fileId: null,
                ownerId,
                label: pickerCurrent.label
              })
            }
          >
            {subcatSelected ? (
              <CheckCircleOutlineIcon
                sx={{ fontSize: 16, color: brandColor() }}
              />
            ) : (
              <FolderOutlinedIcon
                sx={{ fontSize: 16, color: 'text.secondary' }}
              />
            )}
            <Typography
              variant='caption'
              fontWeight={600}
              color={subcatSelected ? brandColor() : 'text.secondary'}
            >
              {subcatSelected
                ? t('Selected — place here')
                : t('Select this folder')}
            </Typography>
          </Box>

          {/* Custom directories below */}
          {files.map((file) => (
            <SelectableRow
              key={file.id}
              label={file.name}
              meta={t('Custom directory')}
              selected={destination?.fileId === file.id}
              onClick={() =>
                setDestination({
                  subcategoryId: subcategory.id,
                  fileId: file.id,
                  ownerId,
                  label: file.name
                })
              }
            />
          ))}

          {files.length === 0 && (
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant='caption' color='text.secondary'>
                {t('No custom directories in this category.')}
              </Typography>
            </Box>
          )}
        </>
      )
    }

    return null
  }

  // ---------------------------------------------------------------------------
  // Execute
  // ---------------------------------------------------------------------------
  const handleConfirm = async () => {
    if (!destination) return
    setLoading(true)
    setError(null)

    try {
      const copyRes = await fetch(apiUrl('CopyDocument'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentId: document.id,
          targetSubcategoryId: destination.subcategoryId,
          targetFileId: destination.fileId ?? null,
          targetAssignedTo: destination.ownerId
        })
      })
      if (!copyRes.ok) throw new Error(t('Copy failed. Please try again.'))

      if (mode === 'move') {
        const params = {
          ...document,
          loggedUserId: userId,
          brandLabel: window.$brand
        }
        const delRes = await fetch(apiUrl('DeleteDocument'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params)
        })
        if (!delRes.ok)
          throw new Error(
            t(
              'File copied but could not remove original. Please delete it manually.'
            )
          )
        onDeleteDoc?.(document.id)
      }

      setDone(true)
      setTimeout(() => {
        onSuccess?.(mode)
        onClose()
      }, 1400)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Breadcrumbs for the picker nav bar
  // ---------------------------------------------------------------------------
  const pickerBreadcrumbs = [
    { label: t('All directories'), index: -1 },
    ...pickerStack.map((e, i) => ({ label: e.label, index: i }))
  ]

  const isCopy = mode === 'copy'
  const Icon = isCopy ? ContentCopyOutlinedIcon : DriveFileMoveOutlinedIcon
  const actionLabel = isCopy ? t('Copy here') : t('Move here')
  const titleLabel = isCopy ? t('Copy document') : t('Move document')

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: { borderRadius: brandRadius() === 0 ? 0 : 3, overflow: 'hidden' }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Icon sx={{ color: brandColor(), fontSize: 20 }} />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant='subtitle1' fontWeight={700} lineHeight={1.2}>
            {titleLabel}
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            noWrap
            display='block'
          >
            {document.filename}
          </Typography>
        </Box>
        {!loading && (
          <IconButton size='small' onClick={onClose}>
            <CloseIcon fontSize='small' />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent
        sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 340 }}
      >
        {done ? (
          // Success state
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              p: 4
            }}
          >
            <CheckCircleOutlineIcon
              sx={{ fontSize: 52, color: 'success.main' }}
            />
            <Typography variant='subtitle1' fontWeight={600}>
              {isCopy ? t('Document copied!') : t('Document moved!')}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Picker nav bar */}
            <Box
              sx={{
                px: 2,
                py: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'grey.50'
              }}
            >
              {pickerStack.length > 0 && (
                <IconButton
                  size='small'
                  onClick={() => popTo(pickerStack.length - 2)}
                  sx={{ flexShrink: 0 }}
                >
                  <ArrowBackIcon fontSize='small' />
                </IconButton>
              )}
              <Breadcrumbs
                separator='›'
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
                  '& .MuiBreadcrumbs-li': { minWidth: 0 }
                }}
              >
                {pickerBreadcrumbs.map((item, i) => {
                  const isLast = i === pickerBreadcrumbs.length - 1
                  return isLast ? (
                    <Typography
                      key={i}
                      variant='caption'
                      fontWeight={600}
                      color='text.primary'
                      noWrap
                    >
                      {item.label}
                    </Typography>
                  ) : (
                    <Link
                      key={i}
                      component='button'
                      variant='caption'
                      color='text.secondary'
                      underline='hover'
                      onClick={() =>
                        item.index === -1 ? reset() : popTo(item.index)
                      }
                      sx={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      {item.label}
                    </Link>
                  )
                })}
              </Breadcrumbs>
            </Box>

            {/* Picker rows */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
              {renderPickerLevel()}
            </Box>

            {/* Selected destination summary */}
            {destination && (
              <>
                <Divider />
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    bgcolor: `${brandColor()}0d`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Icon
                    sx={{ fontSize: 16, color: brandColor(), flexShrink: 0 }}
                  />
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ flexGrow: 1 }}
                  >
                    {isCopy ? t('Copy to') : t('Move to')}:{' '}
                    <strong>{destination.label}</strong>
                  </Typography>
                </Box>
              </>
            )}

            {error && (
              <Alert severity='error' sx={{ m: 2, mt: 0 }}>
                {error}
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      {!done && (
        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Button onClick={onClose} disabled={loading}>
            {t('Cancel')}
          </Button>
          <Button
            variant='contained'
            disabled={!destination || loading}
            onClick={handleConfirm}
            startIcon={
              loading ? (
                <CircularProgress size={14} sx={{ color: 'inherit' }} />
              ) : (
                <Icon />
              )
            }
            sx={{ bgcolor: brandColor(), borderRadius: brandRadius() }}
          >
            {loading ? t('Processing...') : actionLabel}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  )
}

export default MoveOrCopyDialog
